import React, {
  FormEvent, useCallback, useEffect, useState,
} from 'react';
import Box from '@mui/material/Box';
import { translations } from 'src/constants/translations';
import { ButtonApp } from 'src/components/Button';
import { Breadcrumbs, ICrumbs } from 'src/components/profileComponents/Breadcrumbs';
import { pathList } from 'src/containers/Profile/routeList';
import { useParams } from 'react-router-dom';
import { useMarketplaces } from 'src/hooks/marketplace';
import { Loader } from 'src/components/Loader';
import { ReactSortable, Sortable, Store } from 'react-sortablejs';
import { ComplexTile } from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents';
import { useMarketplaceTile } from 'src/hooks/marketplace/useMarketplaceTile';
import {
  IMarketplaceComplexTileConfig,
  IMarketplaceTilesConfigComplex,
} from 'src/typings/marketplace';
import { RandomInteger } from 'src/helpers/randomInteger';
import { TileHeader } from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents/TileHeader';
import { TileLocation } from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents/TileLocation';
import {
  TileMinMaxRange,
} from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents/TileMinMaxRange';
import {
  TileProjectTimeline,
} from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents/TileProjectTimeline';
import {
  TileUnitsBreakdown,
} from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents/TileUnitsBreakdown';
import {
  TileAvailabilityCounter,
} from 'src/components/profileComponents/Marketplace/components/ProjectsTiles/compopents/TileAvailabilityCounter';
import {
  IDevelopmentSpecificationPropChange,
} from 'src/components/profileComponents/Complexes/components/ProjectSpecification/types';
import { IComplexProperty } from 'src/components/profileComponents/Marketplace/types';
import { useToastr } from 'src/hooks/useToastr';
import { useStyles } from './styles';

export const ProjectsTiles: React.FC = () => {
  const { id }: { id: string } = useParams();
  const { getMarketplace } = useMarketplaces();
  const marketplace = getMarketplace(id);
  const { toastrClear, toastrSuccess } = useToastr();
  const {
    tilesConfig, saveTilesConfig, tilesConfigLoading, refetch,
  } = useMarketplaceTile(marketplace);
  const [config, setConfig] = useState<IMarketplaceComplexTileConfig>();
  const [collapseAllComplexes, setCollapseAllComplexes] = useState<boolean>(false);
  const [complexPropsOptions, setComplexPropsOptions] = React.useState<IDevelopmentSpecificationPropChange<IComplexProperty>[]>([]);
  const styles = useStyles();

  useEffect(() => {
    if (marketplace?.complexProperties) {
      setComplexPropsOptions(marketplace.complexProperties.map((item) => ({ label: item.translates?.[marketplace.language] || item.systemName, value: item })));
    }
  }, [marketplace]);

  useEffect(() => {
    if (tilesConfig) {
      setConfig(tilesConfig);
    }
  }, [tilesConfig]);

  const activeProjectsCount = tilesConfig?.complexOrderings.filter((item) => item.isActive).length;

  const onSave = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (config) {
      saveTilesConfig.mutateAsync({ dataToSave: config })
        .then(() => {
          refetch();
          toastrClear();
          toastrSuccess();
        });
    }
  }, [config]);

  const removeTag = useCallback((configIndex: number, tagIndex: number) => {
    setConfig((current) => (current ? ({
      ...current,
      complexOrderings: current.complexOrderings.map((complex, index) => (index === configIndex ? ({
        ...complex,
        tags: complex.tags.filter((_, indexToRemove) => indexToRemove !== tagIndex),
      }) : complex)),
    }) : current));
  }, []);

  const addTag = useCallback((configIndex: number) => {
    setConfig((current) => (current ? ({
      ...current,
      complexOrderings: current.complexOrderings.map((complex, index) => (index === configIndex ? ({
        ...complex,
        tags: [...complex.tags, {
          id: RandomInteger(),
          sortOrder: complex.tags.length,
          text: null,
          iconFileUri: null,
        }],
      }) : complex)),
    }) : current));
  }, []);

  const updateComplex = useCallback((complex: IMarketplaceTilesConfigComplex) => {
    setConfig((current) => (current ? ({
      ...current,
      complexOrderings: current.complexOrderings.map((item) => (item.id === complex.id ? complex : item)),
    }) : current));
  }, []);

  const updateConfigSection = <K extends keyof IMarketplaceComplexTileConfig>(
    field: K,
    value: IMarketplaceComplexTileConfig[K],
  ) => {
    setConfig((current) => (current ? ({ ...current, [field]: value }) : current));
  };

  if (!marketplace || tilesConfigLoading) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={4}
      >
        <Loader />
      </Box>
    );
  }

  const crumbs: ICrumbs[] = [
    {
      name: translations.marketplace,
      url: `/profile/${pathList.marketplace}`,
    },
    {
      name: `${marketplace?.name[marketplace?.language]} set-up`,
      url: `/profile/${pathList.marketplace}/${marketplace?.id}`,
    },
    {
      name: 'Projects tile',
    },
  ];
  return (
    <Box py={5} px={4}>
      <form onSubmit={onSave}>
        <Box mb={8} display="flex" flexWrap="nowrap">
          <Box fontSize="32px" mb={1} fontWeight={700} mr="auto">
            { translations.projectsTitle }
          </Box>
          <div>
            <ButtonApp
              text={translations.saveChanges}
              color="primary"
              variant="contained"
              type="submit"
              disabled={saveTilesConfig.isLoading}
            />
          </div>
        </Box>
        <Box mb={5}>
          <Breadcrumbs crumbs={crumbs} />
        </Box>
        <div className={styles.tilesWrapper}>
          { config && marketplace && (
            <>
              <div className="projects-order">
                <Box mb={8} display="flex" justifyContent="space-between" alignItems="center">
                  <Box fontSize="24px" fontWeight={700}>
                    { translations.projectsOrder }
                  </Box>
                  <Box fontSize="14px">
                    Active
                    {' '}
                    {activeProjectsCount}
                    {' '}
                    of
                    {' '}
                    {config.complexOrderings.length}
                  </Box>
                </Box>
                <ReactSortable
                  animation={150}
                  handle=".drag-handle"
                  list={config.complexOrderings.map((entity) => ({ ...entity, chosen: false }))}
                  onStart={() => setCollapseAllComplexes(true)}
                  onUnchoose={() => setCollapseAllComplexes(false)}
                  setList={(newList, sortable: Sortable | null, store: Store) => {
                    if (store && store.dragging && store.dragging.props && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                      const orderedList = newList.map((item, index) => ({ ...item, sortOrder: index }));
                      setConfig({ ...config, complexOrderings: orderedList });
                    }
                  }}
                >
                  { config.complexOrderings.map((complexOrdering, index) => {
                    return (
                      <div key={complexOrdering.id}>
                        { complexOrdering && (
                        <ComplexTile
                          item={complexOrdering}
                          isDragging={collapseAllComplexes}
                          lang={marketplace.language}
                          removeTag={(tagIndex) => removeTag(index, tagIndex)}
                          addTag={() => addTag(index)}
                          updateComplex={updateComplex}
                        />
                        ) }
                      </div>
                    );
                  }) }
                </ReactSortable>
              </div>
              <div className={styles.projectsDescriptionWrapper}>
                <Box fontSize="24px" fontWeight={700}>
                  { translations.projectsDescription }
                </Box>
                <TileHeader
                  item={config.header}
                  lang={marketplace.language}
                  complexPropertyOptions={complexPropsOptions}
                  onChange={(header) => updateConfigSection('header', header)}
                />
                <TileLocation
                  item={config.location}
                  lang={marketplace.language}
                  onChange={(location) => updateConfigSection('location', location)}
                />
                <TileMinMaxRange
                  item={config.minMaxRange}
                  lang={marketplace.language}
                  complexPropertyOptions={complexPropsOptions}
                  onChange={(minMaxRange) => updateConfigSection('minMaxRange', minMaxRange)}
                />
                <TileProjectTimeline
                  item={config.projectTimeline}
                  lang={marketplace.language}
                  complexPropertyOptions={complexPropsOptions}
                  onChange={(projectTimeline) => updateConfigSection('projectTimeline', projectTimeline)}
                />
                <TileUnitsBreakdown
                  item={config.unitsBreakdown}
                  lang={marketplace.language}
                  complexPropertyOptions={complexPropsOptions}
                  onChange={(unitsBreakdown) => updateConfigSection('unitsBreakdown', unitsBreakdown)}
                />
                <TileAvailabilityCounter
                  item={config.availabilityCounter}
                  lang={marketplace.language}
                  onChange={(availabilityCounter) => updateConfigSection('availabilityCounter', availabilityCounter)}
                />
              </div>
            </>
          ) }
        </div>
      </form>
    </Box>
  );
};
